import { useEffect, useState } from "react";

import type { AdminBookingDetailResponseData, AdminBookingSummary, AdminListPagination } from "@chaochud/contracts";

import { CriticalActionForm, DataTable, PageHeader, StatusPill } from "@/components/admin-ui";
import { adminOpsClient } from "@/lib/api-clients";

const STATUS_TONE: Record<string, "success" | "warning" | "error" | "neutral"> = {
  CONFIRMED: "success",
  COMPLETED: "success",
  PENDING_PAYMENT: "warning",
  CANCELED: "error",
  DISPUTED: "error",
};

export function BookingsPage() {
  const [items, setItems] = useState<AdminBookingSummary[]>([]);
  const [pagination, setPagination] = useState<AdminListPagination | null>(null);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<AdminBookingDetailResponseData | null>(null);

  const load = async (targetPage = page) => {
    const data = await adminOpsClient.listBookings(targetPage);
    setItems(data.items);
    setPagination(data.pagination);
  };

  useEffect(() => {
    void load(page);
  }, [page]);

  return (
    <div className="space-y-6">
      <PageHeader title="จัดการการจอง" />
      <DataTable
        headers={["เลขที่", "ร้าน", "สถานะ", "วันที่เช่า", ""]}
        rows={items.map((booking) => [
          booking.bookingNumber,
          booking.storeName,
          <StatusPill key={`status-${booking.id}`} tone={STATUS_TONE[booking.status] ?? "neutral"}>
            {booking.status}
          </StatusPill>,
          `${booking.rentalStartDate} → ${booking.rentalEndDate}`,
          <button
            key={booking.id}
            type="button"
            className="text-sm font-semibold text-plum hover:underline"
            onClick={async () => setDetail(await adminOpsClient.getBooking(booking.id))}
          >
            ตรวจ
          </button>,
        ])}
        pagination={pagination ?? undefined}
        onPageChange={setPage}
      />
      {detail ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-line bg-slate-surface p-4 text-sm text-ink">
            <p className="font-medium">{detail.bookingNumber}</p>
            <p>
              {detail.status} · reservations ใช้งาน: {detail.activeReservationCount}
            </p>
            <ul className="mt-2 space-y-1 text-xs text-muted">
              {detail.statusHistory.map((entry) => (
                <li key={entry.id}>
                  {entry.fromStatus ?? "∅"} → {entry.toStatus} ({entry.changedByActorType}){" "}
                  {new Date(entry.createdAt).toLocaleString("th-TH")}
                </li>
              ))}
            </ul>
          </div>
          <CriticalActionForm
            title="ยกเลิกการจองโดยแอดมิน"
            targetLabel={detail.bookingNumber}
            currentState={detail.status}
            expectedState="CANCELED"
            impact="ปล่อย calendar reservation และยุติ booking"
            submitLabel="Cancel booking"
            onSubmit={async (payload) => {
              await adminOpsClient.cancelBooking(detail.id, payload);
              setDetail(await adminOpsClient.getBooking(detail.id));
              await load();
            }}
          />
          <CriticalActionForm
            title="ปล่อย reservation ที่ค้าง"
            targetLabel={detail.bookingNumber}
            currentState={`ACTIVE×${detail.activeReservationCount}`}
            expectedState="RELEASED"
            impact="ปล่อย slot ในปฏิทิน"
            submitLabel="Release reservations"
            onSubmit={async (payload) => {
              await adminOpsClient.releaseReservation(detail.id, payload);
              setDetail(await adminOpsClient.getBooking(detail.id));
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
